import { useNow } from '@vueuse/core'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import minMax from 'dayjs/plugin/minMax'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import { ApiLocale, Locale } from '@library/enums'
import { joinText } from '@library/utils/functions'
import { useLocale } from './use-locale'

type DateFormatType = 'EN' | 'ZH'

dayjs.extend(quarterOfYear)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(minMax)

export function useDate() {
  const typeSetting: Record<Locale, DateFormatType> = {
    [Locale.ZH_TW]: 'ZH',
    [Locale.ZH_CN]: 'ZH',
    [Locale.EN]: 'EN'
  }

  const { i18n } = useLocale()

  const { locale, t } = i18n.global

  const apiDateFormat = 'YYYY/MM/DD'
  const apiDateTimeFormat = 'YYYY/MM/DD HH:mm:ss'
  const apiMonthDateFormat = 'YYYY/MM'
  const apiYearFormat = 'YYYY'
  const dateFormat = computed(() => t('Library.Common.DateFormat.Date'))
  const fullDateFormat = computed(() => t('Library.Common.DateFormat.FullDate'))
  const fullTimeFormat = computed(() => t('Library.Common.DateFormat.FullTime'))
  const timeFormat = computed(() => t('Library.Common.DateFormat.Time'))
  const monthFormat = computed(() => t('Library.Common.DateFormat.Month'))
  const yearFormat = computed(() => t('Library.Common.DateFormat.Year'))
  const nowDate = useNow()

  const today = computed(() => dayjs(nowDate.value).format(dateFormat.value))

  const now = computed(() => dayjs(nowDate.value).format(fullTimeFormat.value))

  const oneYearAgo = computed(() =>
    dayjs(nowDate.value).subtract(1, 'year').format(dateFormat.value)
  )

  const formatDate = (
    date: string | Dayjs | null | undefined,
    /** 是否顯示日期的時分秒單位 */
    showDateTimeValue = false
  ): string | null => {
    if (!date) {
      return null
    }

    // 不顯示日期的時分秒單位
    if (!showDateTimeValue) {
      return dayjs(date).format(dateFormat.value)
    }

    if (typeof date !== 'string') {
      return date.format(
        showDateTimeValue ? timeFormat.value : dateFormat.value
      )
    }

    const [dateValue, timeValue] = date.split(' ')

    if (dateValue) {
      let formatDateValue = ''
      const [hour, minute] = timeValue?.split(':') ?? []
      const formatTimeValue = joinText([hour, minute], ':')

      const [year, month, day] = dateValue.split('/')

      if (day) {
        formatDateValue =
          typeSetting[locale.value] === ApiLocale.ZH_TW
            ? `${year}/${month}/${day}`
            : `${month}/${day}/${year}`
      } else {
        formatDateValue =
          typeSetting[locale.value] === ApiLocale.ZH_TW
            ? `${year}/${month}`
            : `${month}/${year}`
      }

      return joinText([formatDateValue, formatTimeValue], ' ')
    }

    return null
  }

  const toDate = (date: string, parse: string = apiDateFormat): Dayjs => {
    return dayjs(date, parse)
  }

  const getMonth = (date: string): number => {
    return dayjs(date).month() + 1
  }

  return {
    dayjs,
    dateFormat,
    timeFormat,
    fullDateFormat,
    fullTimeFormat,
    monthFormat,
    yearFormat,
    formatDate,
    toDate,
    now,
    today,
    oneYearAgo,
    apiDateTimeFormat,
    apiDateFormat,
    apiMonthDateFormat,
    apiYearFormat,
    getMonth
  }
}
